import React, { useState } from "react";
import { Box, Button, Snackbar, Typography } from "@mui/joy";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import FormField from "../../components/steps/FormField";
import PasswordField from "./PasswordField";
import { useDispatch } from "react-redux";
import { adminSignInThunk } from "../../redux/authSlice";
import type { AppDispatch, RootState } from "../../redux/store";
import { useSelector } from "react-redux";

const LoginForm: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [toast, setToast] = useState({
    open: false,
    message: "",
    color: "success" as "success" | "danger",
  });
  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  const loading = useSelector((state: RootState) => state.auth.loading);
  const validationSchema = Yup.object({
    email: Yup.string()
      .email("Enter a valid email")
      .required("Email is required"),
    password: Yup.string().required("Password is required"),
  });

  const formik = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, setFieldError }) => {
      try {
        setToast((prev) => ({ ...prev, open: false }));
        const response = await dispatch(
          adminSignInThunk({ email: values.email, password: values.password })
        ).unwrap();
        setToast({
          open: true,
          message: "Sign-In successful!",
          color: "success",
        });
        localStorage.setItem("adminToken", response?.data?.token);

        navigate("/admin/dashboard");
      } catch (err) {
        setToast((prev) => ({ ...prev, open: false }));
        setTimeout(() => {
          setToast({
            open: true,
            message: "Sign-In failed. Please try again.",
            color: "danger",
          });
        }, 100);
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <Box
      component="form"
      onSubmit={formik.handleSubmit}
      sx={{ width: "100%", maxWidth: 500, mx: "auto" }}
    >
      <Typography level="modalHeading" mb={3}>
        Log in
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          width: "100%",
          height: "70vh",
        }}
      >
        <Box>
          <FormField
            name="email"
            label="Email address"
            placeholder="peter@geek.com"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            touched={formik.touched.email}
            error={formik.errors.email}
            autoComplete="email"
          />

          <PasswordField
            name="password"
            label="Password"
            placeholder="••••••••"
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            touched={formik.touched.password}
            error={formik.errors.password}
            autoComplete="current-password"
          />
        </Box>
        <Button
          type="submit"
          loading={formik.isSubmitting || loading}
          fullWidth
          variant="solid"
        >
          Log in
        </Button>
      </Box>
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        variant="solid"
        color={toast.color}
        sx={{
          color: "#fff",
          px: 2,
          py: 1,
          borderRadius: "8px",
        }}
      >
        <Typography level="body-sm" fontWeight="lg">
          {toast.message}
        </Typography>
      </Snackbar>
    </Box>
  );
};

export default LoginForm;
